/**
 * Catálogo oficial de Mercado Público (espejo de
 * backend/src/modules/integrations/chilecompra/codigos-mp.ts).
 *
 * En la BD `tipoMP` guarda el CÓDIGO de MP (LP, LE, CO, C2…), no la etiqueta.
 * Los filtros deben enviar códigos; la UI muestra la descripción.
 */

export interface TipoMP {
  codigo: string;
  descripcion: string;
  canal: CanalId;
}

export type CanalId =
  | "licitacion-publica"
  | "licitacion-privada"
  | "trato-directo"
  | "convenio-marco"
  | "orden-compra";

export const CANALES: { id: CanalId; label: string; descripcion: string }[] = [
  {
    id: "licitacion-publica",
    label: "Licitación Pública",
    descripcion: "Convocatoria abierta (L1, LE, LP, LS)",
  },
  {
    id: "licitacion-privada",
    label: "Licitación Privada",
    descripcion: "Invitación restringida (A1, B1, B2, E2)",
  },
  {
    id: "trato-directo",
    label: "Trato Directo",
    descripcion: "Cotización o proveedor único (C2, D1)",
  },
  {
    id: "convenio-marco",
    label: "Convenio Marco",
    descripcion: "Compra vía catálogo ChileCompra Express (CO)",
  },
  {
    id: "orden-compra",
    label: "Órdenes de compra",
    descripcion: "Compras menores y OC directas (CA, R1, SE)",
  },
];

export const TIPOS_MP: TipoMP[] = [
  { codigo: "L1", descripcion: "Licitación Pública menor a 100 UTM", canal: "licitacion-publica" },
  { codigo: "LE", descripcion: "Licitación Pública entre 100 y 1.000 UTM", canal: "licitacion-publica" },
  { codigo: "LP", descripcion: "Licitación Pública mayor a 1.000 UTM", canal: "licitacion-publica" },
  { codigo: "LS", descripcion: "Licitación Pública servicios personales especializados", canal: "licitacion-publica" },
  { codigo: "A1", descripcion: "Licitación Privada por LP anterior sin oferentes", canal: "licitacion-privada" },
  { codigo: "B1", descripcion: "Licitación Privada por otras causales", canal: "licitacion-privada" },
  { codigo: "B2", descripcion: "Licitación Privada entre 100 y 1.000 UTM", canal: "licitacion-privada" },
  { codigo: "E2", descripcion: "Licitación Privada menor a 100 UTM", canal: "licitacion-privada" },
  { codigo: "C2", descripcion: "Trato Directo (Cotización)", canal: "trato-directo" },
  { codigo: "D1", descripcion: "Trato Directo por Proveedor Único", canal: "trato-directo" },
  { codigo: "CO", descripcion: "Compra Convenio Marco", canal: "convenio-marco" },
  { codigo: "CA", descripcion: "Orden de Compra sin Resolución", canal: "orden-compra" },
  { codigo: "R1", descripcion: "Orden de Compra menor a 3 UTM", canal: "orden-compra" },
  { codigo: "SE", descripcion: "Orden de Compra sin emisión automática", canal: "orden-compra" },
];

const TIPOS_POR_CODIGO = new Map(TIPOS_MP.map((t) => [t.codigo, t]));

/**
 * Alias de siglas entre endpoints de Mercado Público: `licitaciones.json` usa
 * `CO` para Convenio Marco y `ordenesdecompra.json` usa `CM`. Se resuelven al
 * mismo canal para no partir el catálogo en dos.
 */
const ALIAS_CODIGO: Record<string, string> = { CM: "CO" };

function normalizaCodigo(codigo: string): string {
  const up = codigo.toUpperCase();
  return ALIAS_CODIGO[up] ?? up;
}

/** Canales que Mercado Público NO publica en `licitaciones.json`. */
export const CANALES_SIN_LICITACIONES: CanalId[] = ["convenio-marco"];

export function tipoMPLabel(codigo?: string | null): string {
  if (!codigo) return "—";
  const tipo = TIPOS_POR_CODIGO.get(normalizaCodigo(codigo));
  return tipo ? `${tipo.codigo} — ${tipo.descripcion}` : codigo;
}


export function tipoMPCorto(codigo?: string | null): string {
  if (!codigo) return "—";
  const tipo = TIPOS_POR_CODIGO.get(normalizaCodigo(codigo));
  return tipo ? tipo.descripcion : codigo;
}

/** Códigos MP que pertenecen a un canal (para filtrar con `in`). */
export function codigosDeCanal(canal: CanalId): string[] {
  return TIPOS_MP.filter((t) => t.canal === canal).map((t) => t.codigo);
}

export function canalDeTipo(codigo?: string | null): CanalId | null {
  if (!codigo) return null;
  return TIPOS_POR_CODIGO.get(normalizaCodigo(codigo))?.canal ?? null;
}


export function canalLabel(id?: string | null): string {
  return CANALES.find((c) => c.id === id)?.label ?? (id ?? "—");
}

/** Clases de color (tokens semánticos) por canal para el badge de la tabla. */
export const canalColors: Record<CanalId, string> = {
  "licitacion-publica": "bg-primary/12 text-primary",
  "licitacion-privada": "bg-accent/15 text-accent-foreground",
  "trato-directo": "bg-warning/15 text-warning",
  "convenio-marco": "bg-success/15 text-success",
  "orden-compra": "bg-muted text-muted-foreground",
};

export interface CanalBadge {
  canal: CanalId | null;
  label: string;
  /** Etiqueta corta para espacios reducidos (ej. "Pública"). */
  short: string;
  color: string;
  tooltip: string;
}

const CANAL_SHORT: Record<CanalId, string> = {
  "licitacion-publica": "Pública",
  "licitacion-privada": "Privada",
  "trato-directo": "Trato Directo",
  "convenio-marco": "Convenio Marco",
  "orden-compra": "Orden de compra",
};

/** Marca visual del canal a partir del código MP guardado en `tipoMP`. */
export function canalBadge(codigo?: string | null): CanalBadge {
  const canal = canalDeTipo(codigo);
  if (!canal) {
    return {
      canal: null,
      label: "—",
      short: "—",
      color: "bg-muted/60 text-muted-foreground",
      tooltip: "Canal no informado por Mercado Público",
    };
  }
  return {
    canal,
    label: canalLabel(canal),
    short: CANAL_SHORT[canal],
    color: canalColors[canal],
    tooltip: `Canal: ${canalLabel(canal)} · ${tipoMPLabel(codigo)}`,
  };
}


/** Estados oficiales de una licitación en Mercado Público. */
export const ESTADOS_MP: { value: string; label: string }[] = [
  { value: "publicada", label: "Publicada" },
  { value: "cerrada", label: "Cerrada" },
  { value: "desierta", label: "Desierta" },
  { value: "adjudicada", label: "Adjudicada" },
  { value: "revocada", label: "Revocada" },
  { value: "suspendida", label: "Suspendida" },
];

export function estadoMPLabel(value?: string | null): string {
  if (!value) return "—";
  return ESTADOS_MP.find((e) => e.value === value.toLowerCase())?.label ?? value;
}
